<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    :header="$t('importNewVersion')"
    visible
    modal
    maximizable
    class="popup"
    @keyup.esc="closePopup(false)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(false)"
      />
    </template>
    <div>
      <label for="name" class="col-12">{{
        $t("uploadFile", { file: node.name })
      }}</label>
      <div class="field mt-3">
        <PrimeFileUpload
          v-if="canUpload"
          name="upload_files"
          :choose-label="$t('imports.selectFiles')"
          :upload-label="$t('send')"
          :cancel-label="$t('cancel')"
          :invalid-file-size-message="`${$t('imports.invalidFileSize', {
            name: `{0}`,
            size: `{1}`,
          })}`"
          :invalid-file-type-message="`${$t('imports.invalidFileType', {
            name: `{0}`,
            type: `{1}`,
          })}`"
          :invalid-file-limit-message="
            $t('imports.invalidFileLimit', { size: '{0}' })
          "
          custom-upload
          :multiple="false"
          :max-file-size="500000000"
          :file-limit="1"
          @uploader="alfuploader"
        >
          <template #empty>
            <p>
              {{ $t("dragAndDrop") }}
            </p>
          </template>
        </PrimeFileUpload>
      </div>
    </div>
    <div class="field grid">
      <label for="name" class="col-12 mb-2">{{ $t("addComment") }}</label>
      <div class="col-12 md:col-10 w-full">
        <PrimeTextarea id="comment" v-model="comment" auto-resize :rows="5" />
      </div>
    </div>

    <div class="field grid">
      <div class="grid radio-display">
        <div class="col-12 md:col-6">
          <div class="field-radiobutton mb-0">
            <PrimeRadioButton
              id="majeure"
              v-model="version"
              name="majeure"
              value="MAJEURE"
            />
            <label v-tooltip="``" for="majeure">{{ $t("major") }}</label>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="field-radiobutton mb-0">
            <PrimeRadioButton
              id="mineure"
              v-model="version"
              name="mineure"
              value="MINEURE"
            />
            <label v-tooltip="``" for="mineure">{{ $t("minor") }}</label>
          </div>
        </div>
      </div>
    </div>
  </PrimeDialog>
</template>

<script>
import { useSelectionStore } from "@/stores/selection.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import errorService from "@/services/ErrorService.js";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import Textarea from "primevue/textarea";
import Dialog from "primevue/dialog";
import FileUpload from "primevue/fileupload";
import RadioButton from "primevue/radiobutton";

export default {
  name: "ImportNewVersionPopup",
  components: {
    PrimeButton: Button,
    PrimeTextarea: Textarea,
    PrimeDialog: Dialog,
    PrimeFileUpload: FileUpload,
    PrimeRadioButton: RadioButton,
  },
  directives: {
    tooltip: Tooltip,
  },
  emits: ["close", "message"],
  data() {
    return {
      canUpload: true,
      version: null,
      comment: null,
    };
  },
  mounted() {
    this.node = useSelectionStore().files[0];
    this.version = "MAJEURE";
    this.comment = "";
  },
  methods: {
    closePopup(reload) {
      this.$emit("close", reload);
    },
    alfuploader(fileUploadUploaderEvent) {
      this.$emit("message", {
        severity: "info",
        content: this.$t("uploadLoading"),
      });

      let uploadPromises = [];

      fileUploadUploaderEvent.files.forEach((file) => {
        uploadPromises.push(
          alfrescoNodeService.updateContentNode(
            this.node.id,
            file,
            this.version === "MAJEURE",
            this.comment,
          ),
        );
      });
      Promise.allSettled(uploadPromises)
        .then((uploadResponses) => {
          const listUploaded = [];
          const successfulToast = [];
          const listError = [];
          uploadResponses.forEach((upResp) => {
            if (upResp.status === "fulfilled") {
              listUploaded.push(upResp.value);
              successfulToast.push(upResp.value.entry?.name);
            } else {
              listError.push(upResp.reason);
            }
          });
          if (successfulToast.length > 0) {
            this.$emit("message", {
              severity: "success",
              content: this.$t("uploadFinish"),
            });
          }
          if (listError.length > 0) {
            this.$emit("message", errorService.getUploadErrors(listError));
          }

          this.closePopup(listUploaded.length > 0);
        })
        .catch((error) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("uploadUnknownError"),
            detail: error,
            life: 5000,
          });
        });
    },
  },
};
</script>
